import { Component, Element, h, Host, Prop, State, Watch } from "@stencil/core";
import {
    CalendarEventType,
    CalendarEventResponseData,
    CalendarEventDataType,
    CalendarEventKeywordType,
    CalendarEventRegionType,
} from "./interface";
import serializeForm from "../../utils/serializeForm";
import api from "../../utils/api";

/** static base class */
const baseClass = "ux-iws-event-calendar";

/**
 * @slot (default) - can be used to render the initial event list (SEO)
 * @slot form - can be used to provide required request parameters
 * @slot filter - can be used to display content above the filter tags
 * @slot no-results - should be used to display content if no event matches the current filter settings
 */
@Component({
    tag: "ux-iws-event-calendar",
    styleUrl: "event-calendar.scss",
})
export class EventCalendar {
    /** The component's DOM element */
    @Element() element: HTMLUxIwsEventCalendarElement;

    /** The current date (ISO format) */
    @Prop({ reflect: true, mutable: true }) currentDate: string = "";
    /** API endpoint for requesting events */
    @Prop() endpoint: string = "";
    /** Set `eventType` if only events with specific dataType should be displayed */
    @Prop() eventType: CalendarEventDataType = undefined;
    /** Language used to localize dates and texts */
    @Prop() locale: string = "";
    /** If the value is > 0, the results displayed will be limited */
    @Prop() maxEvents: number = 0;
    /** If this flag is set, only the events of the current month are shown */
    @Prop() hideFutureEvents: boolean = false;
    /** Label for the region dropdown */
    @Prop() regionLabel: string = "";
    /** Placeholder for the region dropdown */
    @Prop() regionPlaceholder: string = "";
    /** Set this flag if the calendar should only render the navigation header */
    @Prop() compact: boolean = false;
    /** Label shown before the hall property of an event */
    @Prop() hallLabel: string = "";
    /** Label shown before the booth property of an event */
    @Prop() boothLabel: string = "";

    /** Local state */
    @State() events: CalendarEventType[] = [];
    @State() eventTypes: string[] = [];
    @State() filteredEvents: CalendarEventType[] = [];
    @State() keywords: CalendarEventKeywordType[] = [];
    @State() regions: CalendarEventRegionType[] = [];
    @State() selectedRegion: string | null = null;
    @State() selectedKeywords: string[] = [];
    @State() selectedDate: Date | null = null;

    /** Form with inputs used for API requests */
    private formElement: HTMLFormElement | null = null;

    @Watch("locale")
    handleLocaleChange() {
        this.init();
    }

    @Watch("events")
    @Watch("selectedDate")
    @Watch("selectedKeywords")
    @Watch("selectedRegion")
    @Watch("hideFutureEvents")
    updateFilteredEvents() {
        let nextMonthDate: Date | null = null;
        if (this.selectedDate !== null) {
            nextMonthDate = new Date(this.selectedDate);
            nextMonthDate.setMonth(this.selectedDate.getMonth() + 1, 1);
            this.currentDate = this.selectedDate.toISOString().split("T")[0];
        }

        this.filteredEvents = this.events.filter((event) => {
            if (this.selectedDate !== null) {
                if (
                    new Date(event.endDate).getTime() <
                    this.selectedDate.getTime()
                ) {
                    return false;
                }
                if (
                    this.hideFutureEvents &&
                    new Date(event.startDate).getTime() >
                        nextMonthDate.getTime()
                ) {
                    return false;
                }
            }
            if (this.selectedKeywords.length > 0) {
                const eventKeywordValues = event.keywords
                    .map((keyword) => keyword.value)
                    .filter((value) => this.selectedKeywords.includes(value));
                if (eventKeywordValues.length === 0) {
                    return false;
                }
            }
            if (
                typeof this.selectedRegion === "string" &&
                this.selectedRegion !== ""
            ) {
                if (
                    event.country !== undefined &&
                    `${event.country}` !== this.selectedRegion
                ) {
                    return false;
                }
            }
            return true;
        });
    }

    private init = async () => {
        if (this.endpoint === "") {
            return;
        }
        const responseData = await this.requestEvents();
        this.eventTypes = Object.keys(responseData.events).filter(
            (value) => value.length > 1
        );
        const eventIds: number[] = [];
        const onlyUnique = (value: CalendarEventType): boolean => {
            if (eventIds.includes(value.uid)) {
                return false;
            }
            eventIds.push(value.uid);
            return true;
        };
        this.events = Object.values<CalendarEventType[]>(responseData.events)
            .reduce((previousValue, currentValue) => {
                return previousValue.concat(currentValue);
            })
            .filter(onlyUnique)
            .filter(
                (value) =>
                    this.eventType === undefined ||
                    value.dataType === this.eventType
            )
            .sort(
                (a, b) =>
                    new Date(a.startDate).getTime() -
                    new Date(b.startDate).getTime()
            );

        // Initialize keywords
        const keywords: CalendarEventKeywordType[] = [];
        const keywordValues: string[] = [];
        this.events.forEach((event) => {
            event.keywords.forEach((keyword) => {
                if (!keywordValues.includes(keyword.value)) {
                    keywordValues.push(keyword.value);
                    keywords.push(keyword);
                }
            });
        });
        this.keywords = keywords;

        // Initialize regions
        const regions: CalendarEventRegionType[] = [];
        const regionValues: string[] = [];
        this.events.forEach((event) => {
            const regionName = event.organization?.address || "";
            const regionValue = `${event.country || ""}`;
            if (
                event.country === undefined ||
                regionName === undefined ||
                regionValue.length === 0
            ) {
                return;
            }
            if (regionValue !== "" && !regionValues.includes(regionValue)) {
                regionValues.push(regionValue);
                regions.push({
                    name: regionName,
                    value: regionValue,
                });
            }
        });
        this.regions = regions;
    };

    private requestEvents = async (): Promise<CalendarEventResponseData> => {
        const formData = serializeForm(this.formElement);
        const params = new URLSearchParams(formData);
        params.set("locale", this.locale);
        return await api<CalendarEventResponseData>(
            `${this.endpoint}${
                this.endpoint.includes("?") ? "&" : "?"
            }${params.toString()}`
        );
    };

    private handleSheetDateChange = (event: CustomEvent<string>) => {
        this.selectedDate = new Date(event.detail);
    };

    private handleTagFilterChange = (event: Event) => {
        const formElement = (event.target as HTMLElement).querySelector<
            HTMLFormElement
        >("form");
        if (formElement === null) {
            return;
        }
        const formData = serializeForm(formElement);
        const params = new URLSearchParams(formData);
        this.selectedKeywords = params.getAll("keyword");
    };

    private handleRegionChange = (event: CustomEvent) => {
        this.selectedRegion = event.detail;
    };

    componentWillLoad() {
        const currentDate = /^[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(this.currentDate)
            ? new Date(this.currentDate)
            : new Date();
        this.currentDate = currentDate.toISOString().split("T")[0];
        this.selectedDate = new Date(this.currentDate);
    }

    componentDidLoad() {
        window.requestAnimationFrame(this.init);
    }

    render() {
        const filterData = {
            label: "",
            name: "keyword",
            value: "",
            tags: this.keywords.map((eventKeyword) => ({
                label: eventKeyword.label,
                value: eventKeyword.value,
            })),
        };
        const filteredEventsWithLimit =
            this.maxEvents > 0
                ? this.filteredEvents.slice(0, this.maxEvents)
                : this.filteredEvents;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--compact`]: this.compact,
                    [`${baseClass}--no-results`]:
                        this.filteredEvents.length === 0,
                }}
            >
                <div class={`${baseClass}__header`}>
                    <div class={`${baseClass}__filter`}>
                        <form
                            class={`${baseClass}__form`}
                            ref={(el) => (this.formElement = el)}
                            onSubmit={(event) => event.preventDefault()}
                        >
                            <slot name="form" />
                        </form>
                        {this.regions.length > 0 && (
                            <div
                                key="field-regions"
                                class={`${baseClass}__field`}
                            >
                                <ux-select
                                    options={[
                                        {
                                            value: "",
                                            name: this.regionPlaceholder || "",
                                        },
                                        ...this.regions,
                                    ].map((option) => ({
                                        ...option,
                                        selected:
                                            this.selectedRegion ===
                                            option.value,
                                    }))}
                                    label={this.regionLabel}
                                    onUxSelectChange={this.handleRegionChange}
                                />
                            </div>
                        )}
                        <div key="field-filter" class={`${baseClass}__field`}>
                            <slot name="filter" />
                            <ux-iws-search-tag-filter
                                data={filterData}
                                onUxFilterChange={this.handleTagFilterChange}
                                multiSelect={true}
                            />
                        </div>
                    </div>
                    <div class={`${baseClass}__calendar`}>
                        <ux-iws-event-calendar-sheet
                            active-year={this.selectedDate?.getFullYear()}
                            active-month={this.selectedDate?.getMonth()}
                            compact={this.compact}
                            events={this.filteredEvents}
                            locale={this.locale}
                            onUxChange={this.handleSheetDateChange}
                        />
                    </div>
                </div>
                <div class={`${baseClass}__events`}>
                    {filteredEventsWithLimit.map((data) => (
                        <ux-iws-event-calendar-item
                            key={data.uid}
                            data={data}
                            locale={this.locale}
                            hallLabel={this.hallLabel}
                            boothLabel={this.boothLabel}
                        />
                    ))}
                </div>
                <div
                    class={{
                        [`${baseClass}__slot`]: true,
                        [`${baseClass}__hidden`]: this.events.length > 0,
                    }}
                >
                    <slot />
                </div>
                <div class={`${baseClass}__no-results`}>
                    <slot name="no-results" />
                </div>
            </Host>
        );
    }
}
